import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
    Box, Stack, TextField, Button, IconButton, Tooltip, InputAdornment, Chip, Avatar, Typography, MenuItem,
} from '@mui/material';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER } from '../../theme';
import { useCrumbActionsSetter } from '../../utils/crumbActions';
import { card, field, ghostBtn, th, td, EmptyState, SkeletonRows, LoadAlert } from '../uiKit';
import http, { apiErrorMessage } from '../../Api/http';
import { GetEmployeeAttendance, GetMeetingPunches, GetAttendanceEmployeeBefore } from '../../Api/Api';
import useFinancialYear from '../../hooks/useFinancialYear';
import useOrgOptions from '../../hooks/useOrgOptions';
import { toApiDate } from '../../utils/apiFields';
import { initialsFromName, avatarTone } from '../../utils/format';
import { selectUser } from '../../redux/slices/authSlice';

const pad = (n) => String(n).padStart(2, '0');
const isoOf = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const todayIso = () => isoOf(new Date());

// A date from the API (dd-MM-yyyy or yyyy-MM-dd, maybe with a time) as yyyy-MM-dd.
const normDate = (v) => {
    const s = String(v || '').trim();
    let m = /^(\d{2})-(\d{2})-(\d{4})/.exec(s);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    return m ? `${m[1]}-${m[2]}-${m[3]}` : '';
};
const recDate = (r) => normDate(r.date ?? r.attendanceDate ?? r.dayDate ?? r.day);
const fmtDay = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime()) ? { date: iso, week: '' } : {
        date: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
        week: d.toLocaleDateString('en-GB', { weekday: 'short' }),
    };
};

const mins = (t) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
    return m ? (+m[1]) * 60 + (+m[2]) : null;
};
const fmtTime = (t) => {
    const v = mins(t);
    if (v === null) return '';
    const h = Math.floor(v / 60);
    return `${pad(h % 12 || 12)}:${pad(v % 60)} ${h >= 12 ? 'PM' : 'AM'}`;
};
const fmtHM = (n) => {
    const v = Math.round(Number(n) || 0);
    if (v <= 0) return '0m';
    return v >= 60 ? `${Math.floor(v / 60)}h ${v % 60}m` : `${v}m`;
};
const span = (a, b) => { const x = mins(a); const y = mins(b); return x === null || y === null || y <= x ? null : y - x; };

const listOf = (body) => {
    const d = body?.data;
    if (Array.isArray(d)) return d;
    if (d && typeof d === 'object') {
        const arr = d.rows ?? d.items ?? d.meetings ?? d.records;
        if (Array.isArray(arr)) return arr;
    }
    return [];
};

// One employee-day: login (first) to logout (last), the breaks, and the meeting sessions.
const dayFacts = (rec, meet) => {
    const punches = Array.isArray(rec?.punches) ? rec.punches : [];
    const login = punches.find((p) => p.loginTime)?.loginTime || '';
    const logout = [...punches].reverse().find((p) => p.logoutTime)?.logoutTime || '';
    const work = span(login, logout);
    const breaks = (Array.isArray(rec?.breaks) ? rec.breaks : [])
        .map((b) => ({ out: b.breakOutTime || '', in: b.breakInTime || '', min: span(b.breakOutTime, b.breakInTime) }))
        .filter((b) => b.out || b.in);
    const breakMin = breaks.reduce((s, b) => s + (b.min || 0), 0);
    const sessions = Array.isArray(meet?.sessions) ? meet.sessions : [];
    const meetMin = sessions.length ? (Number(meet?.totalMinutes) || sessions.reduce((s, x) => s + (Number(x.durationMinutes) || 0), 0)) : 0;
    return { login, logout, work, breaks, breakMin, sessions, meetMin, status: String(rec?.status || '') };
};

const STATUS_TONE = {
    present: ['#16A34A', '#DCFCE7'], late: ['#B45309', '#FEF3C7'], absent: ['#DC2626', '#FEE2E2'],
    leave: ['#1D4ED8', '#DBEAFE'], on_leave: ['#1D4ED8', '#DBEAFE'], half_day: ['#C2410C', '#FFEDD5'],
};
const StatusTag = ({ status }) => {
    const key = String(status || '').toLowerCase();
    const [c, bg] = STATUS_TONE[key] || ['#64748B', '#F1F5F9'];
    if (!key) return null;
    return <Chip size="small" label={key.replace('_', ' ')} sx={{ height: 20, fontSize: 10.5, fontWeight: 700, textTransform: 'capitalize', color: c, bgcolor: bg }} />;
};

const MiniChip = ({ children, tone = 'violet' }) => {
    const t = tone === 'amber' ? ['#92400E', '#FEF3C7', '#FDE68A'] : tone === 'green' ? ['#047857', '#ECFDF5', '#A7F3D0'] : [PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER];
    return <Chip size="small" label={children} sx={{ height: 24, fontSize: 11.5, fontWeight: 600, color: t[0], bgcolor: t[1], border: `1px solid ${t[2]}` }} />;
};

// One line per break / meeting: number, from -> to, and how long it lasted.
function SessionLine({ n, from, to, minutes, tone, openText }) {
    const accent = tone === 'amber' ? ['#92400E', '#FEF3C7', '#FDE68A'] : [PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER];
    const missing = !from || !to;
    return (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, py: 0.7, borderTop: n > 1 ? '1px dashed #EEF0F6' : 'none' }}>
            <Box sx={{ width: 22, height: 22, borderRadius: '50%', bgcolor: accent[1], border: `1px solid ${accent[2]}`, color: accent[0], fontSize: 11, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{n}</Box>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#334155', whiteSpace: 'nowrap' }}>
                {from ? fmtTime(from) : '—'} <Box component="span" sx={{ color: '#94A3B8', mx: 0.4 }}>→</Box> {to ? fmtTime(to) : '—'}
            </Typography>
            <Chip size="small" label={missing ? openText : fmtHM(minutes)} sx={{ ml: 'auto', height: 22, fontSize: 11.5, fontWeight: 700, color: missing ? '#B45309' : accent[0], bgcolor: missing ? '#FFF7ED' : accent[1], border: `1px solid ${missing ? '#FED7AA' : accent[2]}` }} />
        </Stack>
    );
}

// Filters for a person's days. `unit` says what the number box means (it only shows for those filters).
const DAY_FILTERS = [
    { key: 'all', label: 'All days' },
    { key: 'late', label: 'Late' },
    { key: 'absent', label: 'Absent / on leave' },
    { key: 'half', label: 'Half day' },
    { key: 'nopunch', label: 'No punch' },
    { key: 'nologout', label: 'Missing logout' },
    { key: 'brkOver', label: 'Break more than', unit: 'min', def: '60' },
    { key: 'workUnder', label: 'Working less than', unit: 'hrs', def: '8' },
    { key: 'workOver', label: 'Working more than', unit: 'hrs', def: '9' },
    { key: 'meet', label: 'Had meetings' },
];
const keepDay = (d, key, thr) => {
    const st = d.status.toLowerCase();
    const n = Number(thr);
    switch (key) {
        case 'late': return st === 'late';
        case 'absent': return st === 'absent' || st === 'leave' || st === 'on_leave';
        case 'half': return st === 'half_day';
        case 'nopunch': return !d.login;
        case 'nologout': return Boolean(d.login) && !d.logout;
        case 'brkOver': return Number.isFinite(n) && d.breakMin > n;
        case 'workUnder': return d.work !== null && Number.isFinite(n) && d.work < n * 60;
        case 'workOver': return d.work !== null && Number.isFinite(n) && d.work > n * 60;
        case 'meet': return d.sessions.length > 0;
        default: return true;
    }
};

// `self`: an employee's own page ("My Attendance History"). It opens straight on the signed-in employee's date-wise
// history, with no staff list, and a denied request just shows a message instead of bouncing to the dashboard.
export default function TimesheetTab({ self = false }) {
    const user = useSelector(selectUser);
    const me = useMemo(() => ({
        code: String(user?.loginId || user?.employeeCode || ''), name: user?.name || user?.loginId || 'Me', designation: user?.role || '',
    }), [user]);
    const financialYear = useFinancialYear();
    const { departmentNames } = useOrgOptions({ quiet: true });
    const [date, setDate] = useState(todayIso());
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [listFilter, setListFilter] = useState('all');
    const [listThr, setListThr] = useState('');
    const [dept, setDept] = useState('All');
    const listActive = DAY_FILTERS.find((f) => f.key === listFilter) || DAY_FILTERS[0];

    const [chosen, setPerson] = useState(null);
    const person = self ? me : chosen;
    const quiet = useMemo(() => (self ? { noModuleBounce: true } : {}), [self]);
    const [from, setFrom] = useState(todayIso());
    const [to, setTo] = useState(todayIso());
    const [days, setDays] = useState([]);
    const [dLoading, setDLoading] = useState(false);
    const [dError, setDError] = useState('');
    const [dayFilter, setDayFilter] = useState('all');
    const [thr, setThr] = useState('');
    const activeFilter = DAY_FILTERS.find((f) => f.key === dayFilter) || DAY_FILTERS[0];

    const rangeOk = Boolean(from && to && from <= to);

    const loadDay = useCallback(async () => {
        const d = toApiDate(date);
        if (!d) return;
        setLoading(true); setError('');
        try {
            const [att, meet, roster] = await Promise.allSettled([
                http.get(GetEmployeeAttendance, { params: { fromDate: d, toDate: d } }),
                http.get(GetMeetingPunches, { params: { fromDate: d, toDate: d } }),
                financialYear ? http.get(GetAttendanceEmployeeBefore, { params: { financialYear } }) : Promise.resolve({ data: {} }),
            ]);
            if (att.status === 'rejected' && att.reason?.response?.status !== 404) throw att.reason;
            const records = att.status === 'fulfilled' ? listOf(att.value.data) : [];
            const meetBy = new Map((meet.status === 'fulfilled' ? listOf(meet.value.data) : []).map((m) => [m.employeeCode, m]));
            const byCode = new Map(records.map((r) => [r.employeeCode, r]));
            // Everyone on the roster is listed; people with nothing recorded that day just show dashes.
            const everyone = roster.status === 'fulfilled' && Array.isArray(roster.value.data?.details) ? roster.value.data.details.filter((e) => e.employeeCode) : [];
            const base = everyone.length ? everyone : records;
            const seen = new Set(base.map((e) => e.employeeCode));
            const all = [...base, ...records.filter((r) => !seen.has(r.employeeCode))];
            setRows(all.map((e) => {
                const rec = byCode.get(e.employeeCode);
                return {
                    code: e.employeeCode, name: String(e.name || e.employeeName || rec?.employeeName || '').trim(), designation: e.designation || rec?.designation || '', department: e.department || rec?.department || '',
                    ...dayFacts(rec, meetBy.get(e.employeeCode)),
                };
            }));
        } catch (err) {
            setRows([]);
            setError(apiErrorMessage(err, 'Could not load the timesheet.'));
        }
        setLoading(false);
    }, [date, financialYear]);

    useEffect(() => { if (!self) loadDay(); }, [loadDay, self]);

    const loadPerson = useCallback(async () => {
        if (!person || !rangeOk) return;
        setDLoading(true); setDError('');
        try {
            const params = { fromDate: toApiDate(from), toDate: toApiDate(to) };
            const [att, meet] = await Promise.allSettled([
                http.get(GetEmployeeAttendance, { params, ...quiet }),
                http.get(GetMeetingPunches, { params: { ...params, employeeCode: person.code }, ...quiet }),
            ]);
            if (att.status === 'rejected' && att.reason?.response?.status !== 404) throw att.reason;
            const records = (att.status === 'fulfilled' ? listOf(att.value.data) : []).filter((r) => String(r.employeeCode || '').toUpperCase() === person.code.toUpperCase());
            const meetBy = new Map((meet.status === 'fulfilled' ? listOf(meet.value.data) : []).filter((m) => m.employeeCode === person.code).map((m) => [normDate(m.date), m]));
            const byDate = new Map();
            records.forEach((r) => { const k = recDate(r); if (k) byDate.set(k, r); });
            meetBy.forEach((_, k) => { if (!byDate.has(k)) byDate.set(k, null); });
            setDays([...byDate.entries()].sort((a, b) => b[0].localeCompare(a[0])).map(([k, r]) => ({ date: k, ...dayFacts(r, meetBy.get(k)) })));
        } catch (err) {
            setDays([]);
            setDError(self && err?.response?.status === 403
                ? 'Your attendance history is not enabled for your role yet. Ask your administrator.'
                : apiErrorMessage(err, 'Could not load this person\'s history.'));
        }
        setDLoading(false);
    }, [person, from, to, rangeOk, self, quiet]);

    useEffect(() => { loadPerson(); }, [loadPerson]);

    // Every department of the company, plus any that only appear on a person.
    const departments = useMemo(() => ['All', ...[...new Set([...departmentNames, ...rows.map((r) => r.department).filter(Boolean)])].sort((a, b) => a.localeCompare(b))], [departmentNames, rows]);
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const list = [...rows].sort((a, b) => a.name.localeCompare(b.name))
            .filter((r) => (dept === 'All' || r.department === dept) && keepDay(r, listFilter, listThr));
        return q ? list.filter((r) => [r.name, r.code, r.designation, r.department].some((v) => String(v || '').toLowerCase().includes(q))) : list;
    }, [rows, search, dept, listFilter, listThr]);

    const shownDays = useMemo(() => days.filter((d) => keepDay(d, dayFilter, thr)), [days, dayFilter, thr]);
    const totals = useMemo(() => shownDays.reduce((t, d) => ({
        work: t.work + (d.work || 0), brk: t.brk + d.breakMin, meet: t.meet + d.meetMin, worked: t.worked + (d.work ? 1 : 0),
    }), { work: 0, brk: 0, meet: 0, worked: 0 }), [shownDays]);

    const setCrumbActions = useCrumbActionsSetter();
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        const inPerson = Boolean(person);
        const busy = inPerson ? dLoading : loading;
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {inPerson ? (
                    <>
                        <TextField type="date" label="From" size="small" value={from} onChange={(e) => setFrom(e.target.value)} error={!rangeOk}
                            sx={{ ...field, width: 160, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], bgcolor: '#fff' } }} slotProps={{ inputLabel: { shrink: true } }} />
                        <TextField type="date" label="To" size="small" value={to} onChange={(e) => setTo(e.target.value)} error={!rangeOk}
                            sx={{ ...field, width: 160, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], bgcolor: '#fff' } }} slotProps={{ inputLabel: { shrink: true } }} />
                    </>
                ) : (
                    <TextField type="date" label="Date" size="small" value={date} onChange={(e) => setDate(e.target.value)}
                        sx={{ ...field, width: 160, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], bgcolor: '#fff' } }} slotProps={{ inputLabel: { shrink: true } }} />
                )}
                <Tooltip arrow title="Reload">
                    <span><IconButton onClick={inPerson ? loadPerson : loadDay} disabled={busy} sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', color: '#64748B', bgcolor: '#fff', height: 40, width: 40, '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                        <RefreshRoundedIcon sx={{ fontSize: 19 }} />
                    </IconButton></span>
                </Tooltip>
            </Box>,
        );
        return () => setCrumbActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, person, date, from, to, rangeOk, loading, dLoading, loadDay, loadPerson]);

    // ---------- one person, date by date
    if (person) {
        return (
            <Box sx={{ p: 2 }}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                    {!self && <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => { setPerson(null); setDays([]); }} sx={{ ...ghostBtn, height: 40, px: 2 }}>All staff</Button>}
                    <Avatar sx={{ width: 38, height: 38, ...avatarTone(person.name), fontSize: 13, fontWeight: 700 }}>{initialsFromName(person.name)}</Avatar>
                    <Box>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>{person.name}</Typography>
                        <Typography sx={{ fontSize: 12, color: '#98A0AE' }}>{[person.code, person.designation].filter(Boolean).join(' · ')}</Typography>
                    </Box>
                    <Stack direction="row" sx={{ ml: 'auto', gap: 1, flexWrap: 'wrap' }}>
                        <MiniChip>{totals.worked} day{totals.worked === 1 ? '' : 's'} worked</MiniChip>
                        <MiniChip tone="green">Working {fmtHM(totals.work)}</MiniChip>
                        <MiniChip tone="amber">Breaks {fmtHM(totals.brk)}</MiniChip>
                        <MiniChip>Meetings {fmtHM(totals.meet)}</MiniChip>
                    </Stack>
                </Stack>
                {!rangeOk && <LoadAlert sx={{ mb: 2, borderRadius: '9px' }}>The "to" date must be on or after the "from" date.</LoadAlert>}

                <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, mb: 1.6, flexWrap: 'wrap' }}>
                    <TextField select label="Show" size="small" value={dayFilter}
                        onChange={(e) => { setDayFilter(e.target.value); setThr(DAY_FILTERS.find((f) => f.key === e.target.value)?.def || ''); }}
                        slotProps={{ inputLabel: { shrink: true } }} sx={{ ...field, width: 220, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], bgcolor: '#fff' } }}>
                        {DAY_FILTERS.map((f) => <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>)}
                    </TextField>
                    {activeFilter.unit && (
                        <TextField label={activeFilter.unit === 'min' ? 'Minutes' : 'Hours'} size="small" type="number" value={thr} onChange={(e) => setThr(e.target.value)}
                            slotProps={{ htmlInput: { min: 0, step: activeFilter.unit === 'min' ? 5 : 0.5 }, inputLabel: { shrink: true } }}
                            sx={{ ...field, width: 120, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], bgcolor: '#fff' } }} />
                    )}
                    {dayFilter !== 'all' && <Button onClick={() => { setDayFilter('all'); setThr(''); }} sx={{ ...ghostBtn, height: 40, px: 1.6 }}>Clear</Button>}
                    <Typography sx={{ ml: 'auto', fontSize: 12, color: '#98A0AE' }}>{shownDays.length} of {days.length} day{days.length === 1 ? '' : 's'}</Typography>
                </Stack>

                {dLoading ? <Box sx={{ ...card, p: 0 }}><SkeletonRows count={6} /></Box> : dError ? (
                    <Box sx={{ ...card, p: 2 }}><LoadAlert action={<Button size="small" onClick={loadPerson} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>{dError}</LoadAlert></Box>
                ) : shownDays.length === 0 ? (
                    <Box sx={{ ...card, p: 0 }}><EmptyState icon={ScheduleRoundedIcon} title={days.length ? 'No days match this filter' : 'No records'} hint={days.length ? 'Change or clear the filter.' : 'Nothing was recorded for this person in the chosen dates.'} /></Box>
                ) : (
                    <Stack spacing={1.6}>
                        {shownDays.map((d) => {
                            const f = fmtDay(d.date);
                            return (
                                <Box key={d.date} sx={{ ...card, p: 0, overflow: 'hidden' }}>
                                    <Stack direction="row" sx={{ alignItems: 'center', gap: 2, px: 2.5, py: 1.4, bgcolor: PRIMARY_LIGHT, borderBottom: `1px solid ${PRIMARY_BORDER}`, flexWrap: 'wrap' }}>
                                        <Box sx={{ minWidth: 150 }}>
                                            <Typography sx={{ fontSize: 14.5, fontWeight: 800, color: '#0F172A' }}>{f.date}</Typography>
                                            <Stack direction="row" sx={{ gap: 0.8, alignItems: 'center', mt: 0.2 }}>
                                                <Typography sx={{ fontSize: 11.5, color: '#6B7280' }}>{f.week}</Typography>
                                                <StatusTag status={d.status} />
                                            </Stack>
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#6E6B99', letterSpacing: 0.6 }}>LOGIN – LOGOUT</Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#1E293B' }}>{d.login ? `${fmtTime(d.login)} – ${d.logout ? fmtTime(d.logout) : 'no logout'}` : '—'}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#6E6B99', letterSpacing: 0.6 }}>WORKING HRS</Typography>
                                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>{d.work ? fmtHM(d.work) : '—'}</Typography>
                                        </Box>
                                        <Stack direction="row" sx={{ ml: 'auto', gap: 1, flexWrap: 'wrap' }}>
                                            <MiniChip tone="amber">Breaks {fmtHM(d.breakMin)}</MiniChip>
                                            <MiniChip>Meetings {fmtHM(d.meetMin)}</MiniChip>
                                        </Stack>
                                    </Stack>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
                                        <Box sx={{ px: 2.5, py: 1.6, borderRight: { md: '1px solid #EEF0F6' } }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#92400E', mb: 0.6 }}>BREAKS · {d.breaks.length} · {fmtHM(d.breakMin)}</Typography>
                                            {d.breaks.length === 0
                                                ? <Typography sx={{ fontSize: 13, color: '#C4C9D4', py: 0.6 }}>No breaks taken</Typography>
                                                : d.breaks.map((b, i) => <SessionLine key={i} n={i + 1} from={b.out} to={b.in} minutes={b.min} tone="amber" openText={b.out ? 'No return punch' : 'No break-out punch'} />)}
                                        </Box>
                                        <Box sx={{ px: 2.5, py: 1.6 }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: PRIMARY, mb: 0.6 }}>MEETINGS · {d.sessions.length} · {fmtHM(d.meetMin)}</Typography>
                                            {d.sessions.length === 0
                                                ? <Typography sx={{ fontSize: 13, color: '#C4C9D4', py: 0.6 }}>No meetings</Typography>
                                                : d.sessions.map((x, i) => <SessionLine key={i} n={i + 1} from={x.startTime} to={x.endTime} minutes={x.durationMinutes} tone="violet" openText="In progress" />)}
                                        </Box>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>
        );
    }

    // ---------- all staff, one day
    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                <Stack direction="row" sx={{ p: 1.5, gap: 1, alignItems: 'center', borderBottom: '1px solid #EEF0F6' }}>
                    <TextField size="small" placeholder="Search name, ID, department…" value={search} onChange={(e) => setSearch(e.target.value)}
                        sx={{ ...field, width: { xs: '100%', sm: 320 }, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 40 } }}
                        slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} /></InputAdornment> } }} />
                    <TextField select label="Department" size="small" value={dept} onChange={(e) => setDept(e.target.value)}
                        slotProps={{ inputLabel: { shrink: true } }} sx={{ ...field, width: 190, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 40 } }}>
                        {departments.map((d) => <MenuItem key={d} value={d}>{d === 'All' ? 'All departments' : d}</MenuItem>)}
                    </TextField>
                    <TextField select label="Show" size="small" value={listFilter}
                        onChange={(e) => { setListFilter(e.target.value); setListThr(DAY_FILTERS.find((f) => f.key === e.target.value)?.def || ''); }}
                        slotProps={{ inputLabel: { shrink: true } }} sx={{ ...field, width: 200, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 40 } }}>
                        {DAY_FILTERS.map((f) => <MenuItem key={f.key} value={f.key}>{f.key === 'all' ? 'All staff' : f.label}</MenuItem>)}
                    </TextField>
                    {listActive.unit && (
                        <TextField label={listActive.unit === 'min' ? 'Minutes' : 'Hours'} size="small" type="number" value={listThr} onChange={(e) => setListThr(e.target.value)}
                            slotProps={{ htmlInput: { min: 0, step: listActive.unit === 'min' ? 5 : 0.5 }, inputLabel: { shrink: true } }}
                            sx={{ ...field, width: 110, '& .MuiOutlinedInput-root': { ...field['& .MuiOutlinedInput-root'], height: 40 } }} />
                    )}
                    {(listFilter !== 'all' || dept !== 'All') && <Button onClick={() => { setListFilter('all'); setListThr(''); setDept('All'); }} sx={{ ...ghostBtn, height: 40, px: 1.6 }}>Clear</Button>}
                    <Box sx={{ ml: 'auto', fontSize: 12, color: '#98A0AE' }}>{filtered.length} of {rows.length} staff · click a person for the date-wise history</Box>
                </Stack>
                {loading ? <SkeletonRows count={6} /> : error ? (
                    <Box sx={{ p: 2 }}><LoadAlert action={<Button size="small" onClick={loadDay} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>{error}</LoadAlert></Box>
                ) : filtered.length === 0 ? (
                    <EmptyState icon={ScheduleRoundedIcon} title="No records" hint="Nothing was recorded for this date." />
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box component="table" sx={{ width: '100%', minWidth: 760, borderCollapse: 'collapse' }}>
                            <Box component="thead" sx={{ bgcolor: PRIMARY_LIGHT }}>
                                <Box component="tr">
                                    {['S.NO', 'STAFF MEMBER', 'WORKING HRS', 'BREAK TIME', 'MEETING TIME', ''].map((h) => <Box component="th" key={h || 'go'} sx={{ ...th, px: 2.5 }}>{h}</Box>)}
                                </Box>
                            </Box>
                            <Box component="tbody">
                                {filtered.map((r, i) => (
                                    <Box component="tr" key={r.code} onClick={() => { setFrom(todayIso()); setTo(todayIso()); setDayFilter('all'); setThr(''); setPerson({ code: r.code, name: r.name, designation: r.designation }); }}
                                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#F5F4FC' } }}>
                                        <Box component="td" sx={{ ...td, px: 2.5, fontSize: 13, color: '#9CA3AF' }}>{i + 1}</Box>
                                        <Box component="td" sx={{ ...td, px: 2.5 }}>
                                            <Stack direction="row" sx={{ gap: 1.3, alignItems: 'center' }}>
                                                <Avatar sx={{ width: 34, height: 34, ...avatarTone(r.name), fontSize: 12, fontWeight: 700 }}>{initialsFromName(r.name)}</Avatar>
                                                <Box>
                                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{r.name}</Typography>
                                                    <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{r.code}</Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                        <Box component="td" sx={{ ...td, px: 2.5, fontSize: 14, fontWeight: 800, color: '#0F172A', whiteSpace: 'nowrap' }}>{r.work ? fmtHM(r.work) : '—'}</Box>
                                        <Box component="td" sx={{ ...td, px: 2.5, fontSize: 13.5, fontWeight: 600, color: r.breakMin ? '#92400E' : '#C4C9D4', whiteSpace: 'nowrap' }}>{r.breakMin ? fmtHM(r.breakMin) : '—'}</Box>
                                        <Box component="td" sx={{ ...td, px: 2.5, fontSize: 13.5, fontWeight: 600, color: r.meetMin ? PRIMARY : '#C4C9D4', whiteSpace: 'nowrap' }}>{r.meetMin ? fmtHM(r.meetMin) : '—'}</Box>
                                        <Box component="td" sx={{ ...td, px: 2.5, textAlign: 'right' }}><ChevronRightRoundedIcon sx={{ fontSize: 20, color: '#98A0AE' }} /></Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}
